import { MAX_CUSTOMER_ACCOUNTS, MAX_TRIAL_ACCOUNTS } from '@app/configurations';
import { startOfDay } from 'date-fns';
import { CustomerSummary } from '../models/common.models';
import {
  AccountGroupAllowedRoles,
  Customer,
  CustomerFeature,
  CustomerSegment,
  SubscriptionStatus,
} from '../models/customer.models';
import { STORAGE_KEYS } from '../models/storage-keys.models';
import { UserRole } from '../models/user.models';

export function customerHasNotificationsFeatureEnabled(customer?: Customer): boolean {
  return !!customer?.notifications;
}
export function customerHasMerchantSpringFeature(customer: Customer): boolean {
  return (
    customer.isDemo ||
    (!customer.canceled && !isPreTrial(customer) && customerHasFeatureEnabled(customer, CustomerFeature.MerchantSpring))
  );
}

export function customersHasMinBid(customer: Customer): boolean {
  return (
    customerHasFeatureEnabled(customer, CustomerFeature.MinMaxBid) ||
    customerHasFeatureEnabled(customer, CustomerFeature.MinBid)
  );
}
export function customersHasPGSecondGoal(customer: Customer): boolean {
  return customerHasFeatureEnabled(customer, CustomerFeature.SecondGoalForPG);
}
export function customersHasCHEnterpriseFeature(customer: Customer): boolean {
  return customerHasFeatureEnabled(customer, CustomerFeature.CriterionAdditionAdvanced);
}
export function customerHasFeatureEnabled(customer: Customer, feature: CustomerFeature): boolean {
  return !!customer?.features?.includes(feature);
}

export function hasActiveSubscription(customer: Customer): boolean {
  return (
    !customer?.canceled &&
    !!customer?.subscriptionStatus &&
    ![SubscriptionStatus.Terminated].includes(customer.subscriptionStatus)
  );
}

export function assertAllowedAccountGroupRole(role: UserRole): UserRole {
  return AccountGroupAllowedRoles.includes(role) ? role : 'USER';
}

export function preserveLastUsedCustomerID(customer: Customer) {
  if (!customer.archived) {
    localStorage.setItem(STORAGE_KEYS.CUSTOMER_ID, customer.customerId.toString());
  }
}

export function isCustomerFull(customer: Customer, summary?: CustomerSummary): boolean {
  return !!customer && (summary?.accountCount ?? customer?.accountsCount ?? 0) >= getCustomerAccountLimit(customer);
}

export function isEssentialSegment(customer: Customer): boolean {
  return !customer?.segment || [CustomerSegment.Essential, CustomerSegment.Standard].includes(customer.segment);
}

export function getCustomerAccountLimit(customer: Customer): number {
  return customer.trial && isEssentialSegment(customer) ? MAX_TRIAL_ACCOUNTS : MAX_CUSTOMER_ACCOUNTS;
}

export function isPreTrial(customer: Customer) {
  if (customer?.type === 'AGENCY-CLIENT') {
    return false;
  }

  if (!customer?.trialStart) {
    return !customer?.subscriptionStatus;
  }

  const isFuture = +startOfDay(new Date(customer?.trialStart)) > +startOfDay(new Date());

  return isFuture && !customer?.subscriptionStatus;
}
